import {Dialog as DialogPrimitive} from "@base-ui/react/dialog"
import {cva, type VariantProps} from "class-variance-authority"
import {FA6Icon} from "./FA6Icon"
import {clsx} from "clsx"
import styles from "./Modal.module.css"

enum WorkingTimeTypeCategory {
	AttendanceTime = "working-time-type-category-attendance-time",
	PaidTimeOff = "working-time-type-category-paid-time-off",
	UnpaidTimeOff = "working-time-type-category-unpaid-time-off",
	Vacation = "working-time-type-category-vacation",
	Suspension = "working-time-type-category-suspension",
	BreakTime = "working-time-type-category-break-time"
}

/*
  Optional coloured left edge on the card.
  cva maps each `variation` and `size` value onto a CSS-module class.
*/
const modalContentVariants = cva(undefined, {
	variants: {
		size: {
			xs: styles.sizeXs,
			sm: styles.sizeSm,
			md: undefined,
			lg: styles.sizeLg,
			xl: styles.sizeXl,
			full: styles.sizeFull,
		},
		variation: {
			none: undefined,
			"project-time-billable": styles.variationProjectTimeBillable,
			"project-time-not-billable": styles.variationProjectTimeNotBillable,
			"drive-log-business": styles.variationDriveLogBusiness,
			"drive-log-private": styles.variationDriveLogPrivate,

			// Use the enum as the single source of truth via computed properties
			[WorkingTimeTypeCategory.AttendanceTime]: styles.variationAttendanceTime,
			[WorkingTimeTypeCategory.PaidTimeOff]: styles.variationPaidTimeOff,
			[WorkingTimeTypeCategory.UnpaidTimeOff]: styles.variationUnpaidTimeOff,
			[WorkingTimeTypeCategory.Vacation]: styles.variationVacation,
			[WorkingTimeTypeCategory.Suspension]: styles.variationSuspension,
			[WorkingTimeTypeCategory.BreakTime]: styles.variationBreakTime,
		},
	},
	defaultVariants: {
		size: "md",
		variation: "none",
	},
})

// Let CVA extract the exact types based on the configuration above
// NonNullable ensures we don't accidentally export `null` or `undefined` as valid manual prop types
type ModalSize = NonNullable<VariantProps<typeof modalContentVariants>["size"]>;
type ModalVariation = NonNullable<VariantProps<typeof modalContentVariants>["variation"]>;

function ModalRoot(props: DialogPrimitive.Root.Props) {
	return <DialogPrimitive.Root data-slot="modal" {...props} />
}

function ModalTrigger(props: DialogPrimitive.Trigger.Props) {
	return <DialogPrimitive.Trigger data-slot="modal-trigger" {...props} />
}

function ModalClose(props: DialogPrimitive.Close.Props) {
	return <DialogPrimitive.Close data-slot="modal-close" {...props} aria-label="Close"/>
}

function ModalPortal(props: DialogPrimitive.Portal.Props) {
	return <DialogPrimitive.Portal data-slot="modal-portal" {...props} />
}

function ModalBackdrop({className, ...props}: DialogPrimitive.Backdrop.Props) {
	return (
		<DialogPrimitive.Backdrop
			data-slot="modal-backdrop"
			className={clsx(styles.backdrop, className)}
			{...props}
		/>
	)
}

function ModalContent({
						  className,
						  children,
						  size = "md",
						  variation = "none",
						  showCloseIcon = true,
						  ...props
					  }: DialogPrimitive.Popup.Props &
	VariantProps<typeof modalContentVariants> & {
	/** Render the top-right dismiss button. Defaults to `true`. */
	showCloseIcon?: boolean
}) {
	return (
		<ModalPortal>
			<ModalBackdrop/>
			<DialogPrimitive.Viewport
				data-slot="modal-viewport"
				className={styles.viewport}
			>
				<DialogPrimitive.Popup
					data-slot="modal-content"
					className={clsx(
						styles.content,
						modalContentVariants({size, variation}),
						className,
					)}
					{...props}
				>
					{showCloseIcon && (
						<ModalClose
							data-slot="modal-close"
							className={styles.close}
						>
							<FA6Icon icon="times" variant="light"/>
						</ModalClose>
					)}
					{children}
				</DialogPrimitive.Popup>
			</DialogPrimitive.Viewport>
		</ModalPortal>
	)
}

function ModalHeader({className, ...props}: React.ComponentProps<"div">) {
	return (
		<div data-slot="modal-header" className={clsx(styles.header, className)} {...props} />
	)
}

/* BaseUI default is h2, Limitless default is h4 so we use h4 as the default render */
function ModalTitle({className, render = <h4/>, ...props}: DialogPrimitive.Title.Props) {
	return (
		<DialogPrimitive.Title
			data-slot="modal-title"
			className={clsx(styles.title, className)}
			render={render}
			{...props}
		/>
	)
}

function ModalDescription({className, ...props}: DialogPrimitive.Description.Props) {
	return (
		<DialogPrimitive.Description
			data-slot="modal-description"
			className={clsx(styles.description, className)}
			{...props}
		/>
	)
}

function ModalBody({className, ...props}: React.ComponentProps<"div">) {
	return <div data-slot="modal-body" className={clsx(styles.body, className)} {...props} />
}

function ModalFooter({className, ...props}: React.ComponentProps<"div">) {
	return (
		<div data-slot="modal-footer" className={clsx(styles.footer, className)} {...props} />
	)
}

const CreateModalHandle = DialogPrimitive.createHandle;

const Modal = {
	Root: ModalRoot,
	Trigger: ModalTrigger,
	Close: ModalClose,
	Content: ModalContent,
	Header: ModalHeader,
	Title: ModalTitle,
	Description: ModalDescription,
	Body: ModalBody,
	Footer: ModalFooter,
};

export {Modal, CreateModalHandle};

export {WorkingTimeTypeCategory};

/* best practice to export types so consumers could import them and use them to type their own variavles if needed */
export type {ModalSize, ModalVariation};
